import { notNullish } from '@vueuse/core'
export { getPathFromUrl } from './path'

export function slugify(text: string) {
  return text
    .toString()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-/, '') // Trim - from start of text
    .replace(/-$/, '') // Trim - from end of text
}

export function buildPhoneUri(phone: string) {
  if (phone) {
    return `tel:${phone.replace(/[^0-9+]/g, '')}`
  }
  return ''
}

export function onlyUnique<T>(value: T, index: number, self: Array<T>) {
  return self.indexOf(value) === index
}

export function isValidDate(d: Date) {
  return d instanceof Date && !isNaN(d.getTime())
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export function hasOwnProperty(obj: any, property: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, property)
}

export function formatCurrency(value: number, round = false) {
  const rounded = round ? Math.round(value) : value
  return 'CHF ' + rounded.toLocaleString('de-CH')
}

export function formatDistance(distance: number) {
  if (Math.round(distance) < 1000) {
    return Math.round(distance) + 'm'
  }

  return Math.round(distance / 100) / 10 + 'km'
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export function getQueryParamString(query: any, key: string): string {
  if (hasOwnProperty(query, key)) {
    const v = query[key]
    return v && !Array.isArray(v) ? v : ''
  }
  return ''
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export function getQueryParamNumber(query: any, key: string): number | null {
  if (hasOwnProperty(query, key)) {
    let v = query[key]
    if (Array.isArray(v)) {
      v = v[0]
    }

    if (typeof v === 'string') {
      v = parseFloat(v)
    }

    if (v && !isNaN(v)) {
      return v
    }
  }
  return null
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export function getQueryParamArray(query: any, key: string): string[] {
  if (hasOwnProperty(query, key)) {
    const v = query[key]
    if (v) {
      if (Array.isArray(v)) {
        return v.filter(Boolean)
      } else if (typeof v === 'string') {
        return [v]
      }
    }
  }

  return []
}

/**
 * Type check for falsy values.
 *
 * Used as the callback for array.filter, e.g.
 * items.filter(falsy)
 */
export function falsy<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined
}

/**
 * Given a YouTube URL, return the ID.
 */
export function getYouTubeID(url: string): string | null {
  const regExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[7]?.length === 11 ? match[7] : null
}

export function getLocaleForLanguage(language: string): string {
  if (language === 'de') {
    return 'de-CH'
  } else if (language === 'it') {
    return 'it-CH'
  } else if (language === 'fr') {
    return 'fr-CH'
  } else if (language === 'es') {
    return 'es-CH'
  }

  return 'en-GB'
}

export function formatShortDate(date: Date, language: string) {
  return date.toLocaleString(getLocaleForLanguage(language), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// parse [markdown](https://daringfireball.net/projects/markdown/syntax) style links
export function parseMarkdown(str = ''): string {
  return str
    .replace(/&/g, '&amp;') // html entities
    .replace(/</g, '&lt;') // html entities
    .replace(/>/g, '&gt;') // html entities
    .replace(
      /\[([^[]*)\]\(([^)]*)\)/gi,
      '<a href="$2" target="_blank" rel="noopener" class="link">$1</a>',
    ) // link
}

/**
 * Returns a module that also works for negative numbers.
 *
 * See: https://dustinpfister.github.io/2017/09/02/js-whats-wrong-with-modulo
 */
export function modulo(n: number, m: number) {
  return ((n % m) + m) % m
}

export function getLanguageFromPath(path = ''): string | undefined {
  if (!path) {
    return
  }
  const matches = /^\/(de|fr|en|it|es)(\/|$)/.exec(path)
  return matches?.[1]
}

export function hasDrupalSession(cookie = '') {
  try {
    // Add default basic auth for all backend requests.
    return /SESS[a-z0-9]+/.test(cookie)
  } catch {
    console.log('Failed to determine if has Drupal session.')
    return false
  }
}

export function isValidTimeString(time = ''): boolean {
  return /^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(:[0-5][0-9])?$/.test(time)
}

export function isValidDateString(date = ''): boolean {
  return /^(19|20)\d\d-(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[012])$/.test(date)
}

export function getDateKey(date: Date): string {
  return date.toLocaleString('en-CA', {
    timeZone: 'Europe/Zurich',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

export function getDateParts(v = '') {
  if (v.includes('.')) {
    const [d, m, y] = v
      .split('.')
      .filter(Boolean)
      .map((v) => parseInt(v))

    return { d, m, y }
  } else if (v.includes('/')) {
    const [m, d, y] = v
      .split('/')
      .filter(Boolean)
      .map((v) => parseInt(v))

    return { d, m, y }
  } else if (v.includes('-')) {
    const [y, m, d] = v
      .split('-')
      .filter(Boolean)
      .map((v) => parseInt(v))
    // 2022-01-30
    if (y && y > 1000) {
      return { y, m, d }
    }

    return {}
  }

  return {}
}

export function parseUserInputDate(v: string): Date | undefined {
  const { d, m, y } = getDateParts(v)

  if (d && m && y && y.toString().length === 4) {
    const key = [
      y.toString(),
      m.toString().padStart(2, '0'),
      d.toString().padStart(2, '0'),
    ].join('-')
    const date = new Date(key)
    if (isValidDate(date)) {
      return date
    }
  }
}

/**
 * Check if the string starts with a number.
 */
export function startsWithNumber(v = ''): boolean {
  return /^\d/.test(v)
}

/**
 * Generate a valid HTML ID attribute value for a string.
 *
 * @see https://www.w3.org/TR/html4/types.html#type-id
 */
export function toValidHtmlId(text: string) {
  let id = slugify(text)
  if (startsWithNumber(id)) {
    id = 's-' + id
  }

  return id
}

export function filterNullArray<T>(v?: Array<T | null>): T[] {
  if (!v) return []
  return v.filter(notNullish)
}

// Strip HTML tags and decode entities for plain text
export function stripHtml(html: string): string {
  let text = html
  let tagStart = text.indexOf('<')
  while (tagStart !== -1) {
    const tagEnd = text.indexOf('>', tagStart)
    if (tagEnd === -1) {
      break
    }
    text = text.slice(0, tagStart) + text.slice(tagEnd + 1)
    tagStart = text.indexOf('<')
  }

  return text
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&amp;/g, '&') // Decode &amp;
    .replace(/&lt;/g, '<') // Decode &lt;
    .replace(/&gt;/g, '>') // Decode &gt;
    .replace(/&quot;/g, '"') // Decode &quot;
    .replace(/&#39;/g, "'") // Decode &#39;
    .replace(/\s+/g, ' ') // Collapse multiple whitespace
    .trim()
}
